import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MarkupDialog from "./MarkupDialog";
import { api, fmtErr } from "../../lib/api";
import SelfCheckDialog from "./SelfCheckDialog";
import { toast } from "sonner";
import Toolbar from "./Toolbar";
import DrawingTree from "./DrawingTree";
import StudioCanvas from "./StudioCanvas";
import PropertiesPanel from "./PropertiesPanel";
import StatusBar from "./StatusBar";
import ApprovalBar from "./ApprovalBar";
import StudioCopilot from "./StudioCopilot";
import Model3dPanel from "./Model3dPanel";
import SymbologyLibrary from "../symbology/SymbologyLibrary";
import DrawingJobProgress from "./DrawingJobProgress";
import useDrawingJob from "../../hooks/useDrawingJob";
import { withUids, stripForSave, uid, rotatePrim, previewOps, applyOps, LAYER_ORDER } from "./studioUtils";
import { Check, X, Sidebar, SpinnerGap, ShieldWarning } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SIGNOFF = ["lead", "project_manager", "admin"];

export default function DrawingStudio({ projectId }) {
  const [meta, setMeta] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [doc, setDoc] = useState(null);            // full drawing doc
  const [elements, setElements] = useState([]);    // working copy with uids
  const [selection, setSelection] = useState([]);
  const [tool, setTool] = useState("select");
  const [activeLayer, setActiveLayer] = useState("OUTLINE");
  const [layerVis, setLayerVis] = useState({});
  const [showGrid, setShowGrid] = useState(true);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [saveState, setSaveState] = useState("saved");
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [zoomPct, setZoomPct] = useState(100);
  const [me, setMe] = useState(null);
  const [showMfgSheet, setShowMfgSheet] = useState(false);
  const [mfgImgLoaded, setMfgImgLoaded] = useState(false);
  const [checkers, setCheckers] = useState([]);
  const [ghosts, setGhosts] = useState([]);
  const [pendingOps, setPendingOps] = useState(null);
  const [pendingText, setPendingText] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [view3d, setView3d] = useState(false);
  const [compare3d, setCompare3d] = useState(false);
  const [symbolsView, setSymbolsView] = useState(false);
  const [components3d, setComponents3d] = useState([]);
  const [highlight3d, setHighlight3d] = useState(null);
  const [pendingXrefNo, setPendingXrefNo] = useState(null);
  const [markups, setMarkups] = useState([]);
  const [mkDialog, setMkDialog] = useState(null);      // {kind:'2d'|'3d', pos:{…}, target?}
  const [mkDetail, setMkDetail] = useState(null);      // markup record in the comment card
  const [mkPanelOpen, setMkPanelOpen] = useState(false);
  const [pendingMkSel, setPendingMkSel] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const stageRef = useRef(null);
  const fsRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else fsRef.current?.requestFullscreen?.().catch(() => {});
  };
  const canvasApi = useRef({});
  const clipboard = useRef([]);
  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);

  const locked = doc && (doc.status === "approved" || doc.status === "released");

  // ---------- data loading ----------
  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/drawings", { params: { project_id: projectId } });
      setDrawings(data.drawings.filter((d) => d.status !== "superseded"));
      setMeta(data.meta);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [projectId]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/users").then((r) => setCheckers(r.data.filter((u) => SIGNOFF.includes(u.role))))
      .catch(async () => {
        try {
          const p = await api.get(`/projects/${projectId}`);
          setCheckers((p.data.assigned_team || []).filter((m) => SIGNOFF.includes(m.role)));
        } catch { /* no checkers */ }
      });
  }, [projectId]);

  const openDrawing = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/drawings/${d.id}`);
      setDoc(data);
      setElements(withUids(data.elements || []));
      setSelection([]);
      setUndoStack([]); setRedoStack([]);
      setGhosts([]); setPendingOps(null);
      setSaveState(data.status === "approved" || data.status === "released" ? "locked" : "saved");
      dirtyRef.current = false;
      // MFG print is the primary GA view — auto-open it over the schematic when available
      if (d.type === "ga") {
        api.get(`/drawings/${projectId}/print-ga.svg`, { responseType: "text" })
          .then(() => { setMfgImgLoaded(false); setShowMfgSheet(true); })
          .catch(() => {});
      } else {
        setShowMfgSheet(false);
      }
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);

  // 2D↔3D cross-reference: 3D manifest components (balloon no → component key)
  useEffect(() => {
    api.get(`/projects/${projectId}`).then(({ data }) => setComponents3d(data.cad_model?.components || [])).catch(() => {});
  }, [projectId]);

  // markups & comments (2D pins + 3D pins) + org users for owner assignment
  const loadMarkups = useCallback(() => {
    api.get(`/projects/${projectId}/markups`).then(({ data }) => setMarkups(data)).catch(() => {});
  }, [projectId]);
  useEffect(() => {
    loadMarkups();
    api.get("/users").then(({ data }) => setOrgUsers(data)).catch(() => {});
  }, [loadMarkups]);

  // ---------- editing core ----------
  const commit = useCallback((next) => {
    const withIds = next.map((e) => (e.uid ? e : { ...e, uid: uid() }));
    setUndoStack((s) => [...s.slice(-60), elements]);
    setRedoStack([]);
    setElements(withIds);
    if (!locked) { setSaveState("dirty"); dirtyRef.current = true; }
  }, [elements, locked]);

  const undo = useCallback(() => {
    setUndoStack((s) => {
      if (!s.length) return s;
      const prev = s[s.length - 1];
      setRedoStack((r) => [...r, elements]);
      setElements(prev);
      setSelection([]);
      if (!locked) { setSaveState("dirty"); dirtyRef.current = true; }
      return s.slice(0, -1);
    });
  }, [elements, locked]);

  const redo = useCallback(() => {
    setRedoStack((s) => {
      if (!s.length) return s;
      const next = s[s.length - 1];
      setUndoStack((u) => [...u, elements]);
      setElements(next);
      setSelection([]);
      if (!locked) { setSaveState("dirty"); dirtyRef.current = true; }
      return s.slice(0, -1);
    });
  }, [elements, locked]);

  // ---------- save ----------
  const save = useCallback(async () => {
    if (!doc || locked) return;
    setSaveState("saving");
    try {
      const { data } = await api.put(`/drawings/${doc.id}/save`, { elements: stripForSave(elements) }, { timeout: 60000 });
      setSaveState("saved");
      dirtyRef.current = false;
      if (data.status && data.status !== doc.status) {
        setDoc((d) => ({ ...d, status: data.status, signatures: { ...d.signatures, checked: undefined } }));
        toast.info("Drawing was reset to Draft (edited after check).");
      }
      loadList();
    } catch (e) {
      setSaveState("dirty");
      if (e?.response?.status === 409) toast.error("Drawing is locked — create a new revision to edit.");
      else toast.error(fmtErr(e));
    }
  }, [doc, elements, locked, loadList]);

  // ---------- pre-issue gate ----------
  const [gate, setGate] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/drawings/${projectId}/selfcheck`).then(({ data }) => setGate(data)).catch(() => setGate(null));
  }, [projectId]);

  // autosave debounce 3s
  useEffect(() => {
    if (saveState !== "dirty" || locked || !doc) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { if (dirtyRef.current) save(); }, 3000);
    return () => clearTimeout(saveTimer.current);
  }, [saveState, elements, doc, locked, save]);

  // ---------- generate (async job + polling — survives the ingress 60s timeout) ----------
  const { jobList, activeJobs, isGenerating, generate, cancel, retry, dismiss, prioritise } = useDrawingJob(projectId, {
    onTerminal: (job) => {
      if (job.status === "done") {
        toast.success(job.result?.summary || "Drawing generated");
        loadList();
        if (job.job_type === "generate_single" && job.result?.drawing_id && doc?.type === job.drawing_type) {
          openDrawing({ id: job.result.drawing_id });
        }
        if (job.job_type === "print_drawing") setShowMfgSheet(true);
      } else if (job.status === "failed") {
        const msg = job.error?.message || "Drawing generation failed";
        toast.error(msg, { duration: 8000 });
        if (/self-check/i.test(msg)) {
          api.get(`/drawings/${projectId}/selfcheck`).then(({ data }) => {
            setGate(data);
            setGateOpen(true);
          }).catch(() => setGateOpen(true));
        }
      }
    },
  });

  const generateAll = () => { generate("generate_all").catch((e) => toast.error(fmtErr(e))); };
  const generateOne = (type) => { generate("generate_single", type).catch((e) => toast.error(fmtErr(e))); };

  const treeBusy = isGenerating
    ? (activeJobs.find((j) => j.job_type === "generate_single")?.drawing_type || "all")
    : "";

  const openMfgSheet = async () => {
    try {
      await api.get(`/drawings/${projectId}/print-ga.svg`, { responseType: "text" });
      setMfgImgLoaded(false);
      setShowMfgSheet(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.info("Manufacturing sheet not generated yet — starting the 3D-derived print job…");
        generate("print_drawing").catch((err) => toast.error(fmtErr(err)));
      } else {
        toast.error(fmtErr(e));
      }
    }
  };

  // ---------- approval workflow ----------
  const statusAction = async (action, extra) => {
    const { data } = await api.post(`/drawings/${doc.id}/status`, { action, ...extra });
    setDoc((d) => ({ ...d, ...data }));
    if (data.status === "approved" || data.status === "released") setSaveState("locked");
    toast.success(`Status → ${data.status}`);
    loadList();
  };

  const newRevision = async (description) => {
    const { data } = await api.post(`/drawings/${doc.id}/revisions`, { description }, { timeout: 60000 });
    toast.success(`Created Rev ${data.rev_label}`);
    await loadList();
    openDrawing({ id: data.id });
  };

  // ---------- AI ghost preview ----------
  const onOps = (ops) => {
    setPendingOps(ops);
    setGhosts(previewOps(elements, ops));
    if (!previewOps(elements, ops).length) toast.info("Couldn't match any canvas elements for those operations.");
  };
  const applyGhosts = () => {
    commit(applyOps(elements, pendingOps));
    setGhosts([]); setPendingOps(null);
    toast.success("AI changes applied");
  };
  const cancelGhosts = () => { setGhosts([]); setPendingOps(null); };

  // ---------- toolbar actions ----------
  const onAction = (action) => {
    const sel = elements.filter((e) => selection.includes(e.uid));
    switch (action) {
      case "zoom_in": canvasApi.current?.zoom && canvasApi.current.zoom(1.25); break;
      case "zoom_out": canvasApi.current?.zoom && canvasApi.current.zoom(1 / 1.25); break;
      case "fit": canvasApi.current?.fit && canvasApi.current.fit(); break;
      case "grid": setShowGrid((g) => !g); break;
      case "save": save(); break;
      case "undo": undo(); break;
      case "redo": redo(); break;
      case "delete":
        if (sel.length && !locked) { commit(elements.filter((e) => !selection.includes(e.uid))); setSelection([]); }
        break;
      case "copy":
        if (sel.length && !locked) {
          const copies = sel.map((e) => ({ ...e, uid: undefined }));
          const shifted = copies.map((e) => {
            const q = { ...e };
            if (q.kind === "line") { q.x1 += 10; q.y1 += 10; q.x2 += 10; q.y2 += 10; }
            else if (q.kind === "rect") { q.x += 10; q.y += 10; }
            else if (q.kind === "circle") { q.cx += 10; q.cy += 10; }
            else if (q.kind === "text") { q.x += 10; q.y += 10; }
            else if (q.kind === "poly") { q.pts = q.pts.map(([a, b]) => [a + 10, b + 10]); }
            return q;
          });
          commit([...elements, ...shifted]);
        }
        break;
      case "rotate": {
        if (!sel.length || locked) break;
        const angle = parseFloat(window.prompt("Rotate by degrees (CCW positive):", "90"));
        if (!angle || isNaN(angle)) break;
        commit(elements.map((e) => (selection.includes(e.uid) ? rotatePrim(e, -angle) : e)));
        setSelection([]);
        break;
      }
      case "export_dxf": window.open(`${BASE}/api/drawings/${doc.id}/dxf`, "_blank"); break;
      case "export_pdf": window.open(`${BASE}/api/drawings/${doc.id}/pdf`, "_blank"); break;
      case "export_png": {
        const uri = stageRef.current?.toDataURL({ pixelRatio: 2 });
        if (uri) {
          const a = document.createElement("a");
          a.href = uri; a.download = `${doc.drawing_no}.png`; a.click();
        }
        break;
      }
      default: break;
    }
  };

  // ---------- properties edit ----------
  const onPropsChange = (uids, patch) => {
    if (locked) return;
    commit(elements.map((e) => (uids.includes(e.uid) ? { ...e, ...patch } : e)));
  };

  // ---------- keyboard shortcuts ----------
  useEffect(() => {
    const h = (e) => {
      if (/INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (e.ctrlKey || e.metaKey) {
        if (k === "z") { e.preventDefault(); undo(); }
        else if (k === "y") { e.preventDefault(); redo(); }
        else if (k === "s") { e.preventDefault(); save(); }
        else if (k === "c") { clipboard.current = elements.filter((el) => selection.includes(el.uid)); }
        else if (k === "v" && clipboard.current.length && !locked) {
          const pasted = clipboard.current.map((el) => {
            const q = { ...el, uid: undefined };
            if (q.kind === "line") { q.x1 += 12; q.y1 += 12; q.x2 += 12; q.y2 += 12; }
            else if (q.kind === "rect") { q.x += 12; q.y += 12; }
            else if (q.kind === "circle") { q.cx += 12; q.cy += 12; }
            else if (q.kind === "text") { q.x += 12; q.y += 12; }
            else if (q.kind === "poly") { q.pts = q.pts.map(([a, b]) => [a + 12, b + 12]); }
            return q;
          });
          commit([...elements, ...pasted]);
        }
        return;
      }
      if (k === "escape") { setTool("select"); setSelection([]); setPendingText(null); }
      else if (k === "delete" || k === "backspace") onAction("delete");
      else if (k === "l") setTool("line");
      else if (k === "c") setTool("circle");
      else if (k === "r") setTool("rect");
      else if (k === "t") setTool("text");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  // 2D↔3D cross-reference: a selected GA balloon (numeric text on the front view,
  // x < 900 excludes the right-column legend) maps to a 3D manifest component
  const xrefComp = useMemo(() => {
    if (view3d || selection.length !== 1 || !components3d.length) return null;
    const el = elements.find((e) => e.uid === selection[0]);
    if (!el || el.kind !== "text" || el.layer !== "TEXT") return null;
    if (!/^\d{1,2}$/.test(String(el.s ?? "")) || (el.x ?? 9999) > 900) return null;
    return components3d.find((c) => String(c.no) === String(el.s)) || null;
  }, [selection, elements, view3d, components3d]);

  useEffect(() => {
    if (pendingXrefNo == null || view3d || !elements.length) return;
    const el = elements.find((e) => e.kind === "text" && String(e.s) === String(pendingXrefNo) && (e.x ?? 9999) < 900);
    if (el) {
      setSelection([el.uid]);
      setPendingXrefNo(null);
      toast.success(`Balloon ${pendingXrefNo} selected on the GA sheet`);
    }
  }, [elements, pendingXrefNo, view3d]);

  // placing a 2D markup pin needs the base drawing — auto-close the MFG overlay
  useEffect(() => {
    if (tool === "markup_pin" && showMfgSheet) {
      setShowMfgSheet(false);
      toast.info("MFG sheet closed — click on the drawing to place the pin");
    }
  }, [tool, showMfgSheet]);

  // ---------- markups & comments ----------
  const saveMarkup = async ({ comment, owner_id, severity }) => {
    if (!mkDialog) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/markups`, {
        kind: mkDialog.kind, drawing_id: mkDialog.kind === "2d" ? doc?.id : null,
        target: mkDialog.target || null, position: mkDialog.pos,
        comment, owner_id, severity });
      if (mkDialog.kind === "2d") {
        const { x, y } = mkDialog.pos;
        commit([...elements,
          { kind: "circle", layer: "MARKUP", cx: x, cy: y, r: 14, uid: `mk_${data.id}` },
          { kind: "text", layer: "MARKUP", x: x - 8, y: y + 4, s: `#${data.seq}`, size: 11, uid: `mkt_${data.id}` }]);
        setTool("select");
        setSelection([`mk_${data.id}`]);
      }
      setMkDialog(null);
      setMkDetail(data);
      loadMarkups();
      toast.success(`Markup #${data.seq} added${data.owner_name ? ` · assigned to ${data.owner_name}` : ""}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setMarkupStatus = async (mk, status) => {
    try {
      await api.patch(`/markups/${mk.id}`, { status, resolution: status === "resolved" ? (mk.resolution || "Resolved") : undefined });
      loadMarkups();
      setMkDetail((d) => (d && d.id === mk.id ? { ...d, status } : d));
      toast.success(status === "resolved" ? `Markup #${mk.seq} resolved` : `Markup #${mk.seq} reopened`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteMarkup = async (mk) => {
    try {
      await api.delete(`/markups/${mk.id}`);
      if (mk.kind === "2d") commit(elements.filter((e) => e.uid !== `mk_${mk.id}` && e.uid !== `mkt_${mk.id}`));
      setMkDetail(null);
      loadMarkups();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openMarkup = async (mk) => {
    setMkPanelOpen(false);
    setMkDetail(mk);
    if (mk.kind === "3d") {
      if (mk.target) setHighlight3d(mk.target);
      setView3d(true);
      return;
    }
    if (doc?.id === mk.drawing_id) {
      const el = elements.find((e) => e.uid === `mk_${mk.id}`);
      if (el) setSelection([el.uid]);
      return;
    }
    const d = drawings.find((x) => x.id === mk.drawing_id);
    if (d) {
      setPendingMkSel(mk.id);
      setView3d(false);
      await openDrawing(d);
    }
  };

  // after a drawing opens, select a pending markup pin
  useEffect(() => {
    if (!pendingMkSel || view3d || !elements.length) return;
    const el = elements.find((e) => e.uid === `mk_${pendingMkSel}`);
    if (el) { setSelection([el.uid]); setPendingMkSel(null); }
  }, [elements, pendingMkSel, view3d]);

  // selecting a markup pin on the sheet opens its comment card
  useEffect(() => {
    const mkEl = elements.find((e) => selection.includes(e.uid) && String(e.uid).startsWith("mk_"));
    if (mkEl) {
      const mk = markups.find((m) => `mk_${m.id}` === mkEl.uid);
      if (mk && mkDetail?.id !== mk.id) setMkDetail(mk);
    }
  }, [selection, elements, markups]); // eslint-disable-line

  const revisions = doc ? drawings.filter((d) => d.type === doc.type) : [];
  const selEls = elements.filter((e) => selection.includes(e.uid));

  return (
    <div className="flex flex-col h-[calc(100vh-170px)] min-h-[560px] bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="drawing-studio">
      <Toolbar tool={tool} setTool={setTool} onAction={onAction} locked={locked} disabled={!doc}
        revisions={revisions} revId={doc?.id} onRevChange={(id) => openDrawing({ id })}
        layerVis={layerVis} activeLayer={activeLayer}
        onToggleLayer={(l) => setLayerVis((v) => ({ ...v, [l]: v[l] === false }))}
        onSetActiveLayer={setActiveLayer} zoomPct={zoomPct} onToggleFs={toggleFs} isFs={isFs} />
      <ApprovalBar drawing={doc} me={me} checkers={checkers} onAction={statusAction} onNewRevision={newRevision} busy={isGenerating} />

      {gate && gate.status !== "pass" && gate.status !== "not_applicable" && (
        <div data-testid="gate-banner"
          className={`flex shrink-0 items-center gap-2 border-b px-3 h-9 ${gate.status === "block"
            ? "border-[var(--c-red)]/40 bg-[var(--c-red)]/10" : "border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10"}`}>
          <ShieldWarning size={13} className={gate.status === "block" ? "text-[var(--c-red)]" : "text-[var(--c-amber)]"} />
          <span className="text-[11px] text-[var(--c-text)]">
            Pre-issue self-check — {gate.summary}
            {gate.status === "block" ? " · no sheet will be issued until each one is resolved or accepted" : ""}
          </span>
          <button data-testid="gate-review-btn" onClick={() => setGateOpen(true)}
            className="ml-auto h-6 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
            Review {gate.blocking?.length ? `${gate.blocking.length} blocking` : "findings"}
          </button>
        </div>
      )}
      <SelfCheckDialog projectId={projectId} open={gateOpen} onOpenChange={setGateOpen}
        gate={gate} onGate={setGate} />

      {ghosts.length > 0 && (
        <div className="flex items-center gap-2 px-3 h-9 bg-[var(--c-accent)]/15 border-b border-[var(--c-accent)]/40 shrink-0" data-testid="ghost-banner">
          <span className="text-[11px] text-[var(--c-text)]">AI preview — {pendingOps?.length} operation(s) shown as blue ghosts.</span>
          <button data-testid="ghost-apply" onClick={applyGhosts}
            className="h-6 px-2.5 rounded-sm bg-[var(--c-green)] text-white text-[11px] flex items-center gap-1"><Check size={11} /> Apply</button>
          <button data-testid="ghost-cancel" onClick={cancelGhosts}
            className="h-6 px-2.5 rounded-sm bg-[var(--c-red)]/20 text-[var(--c-red)] text-[11px] flex items-center gap-1 border border-[var(--c-red)]/40"><X size={11} /> Cancel</button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {leftOpen && (
          <div className="w-[250px] shrink-0 border-r border-[var(--c-border)] bg-[var(--c-surface)] flex flex-col min-h-0">
            <div className="flex border-b border-[var(--c-border)] shrink-0" data-testid="view-mode-toggle">
              <button data-testid="view-2d-btn" onClick={() => { setView3d(false); setCompare3d(false); setSymbolsView(false); }}
                className={`flex-1 h-8 text-[11px] font-medium transition-colors duration-150 ${!view3d && !symbolsView ? "text-[var(--c-text)] bg-[var(--c-accent)]/15 border-b-2 border-[var(--c-accent)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                2D Drawings
              </button>
              <button data-testid="view-3d-btn" onClick={() => { setView3d(true); setCompare3d(false); setSymbolsView(false); }}
                className={`flex-1 h-8 text-[11px] font-medium transition-colors duration-150 ${view3d && !compare3d && !symbolsView ? "text-[var(--c-text)] bg-[var(--c-accent)]/15 border-b-2 border-[var(--c-accent)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                3D Model
              </button>
              <button data-testid="view-symbols-btn" onClick={() => setSymbolsView(true)}
                className={`flex-1 h-8 text-[11px] font-medium transition-colors duration-150 ${symbolsView ? "text-[var(--c-text)] bg-[var(--c-accent)]/15 border-b-2 border-[var(--c-accent)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                Symbols
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <DrawingTree meta={meta} drawings={drawings} selId={doc?.id} projectId={projectId}
                onSelect={openDrawing} onGenerate={generateOne} onGenerateAll={generateAll}
                onRefresh={loadList} busy={treeBusy} />
            </div>
          </div>
        )}
        <div ref={fsRef} className="flex flex-col flex-1 min-w-0 relative bg-[var(--c-bg)]">
          <button data-testid="toggle-left" onClick={() => setLeftOpen((v) => !v)}
            className="absolute left-1 top-1 z-20 h-6 w-6 flex items-center justify-center rounded-sm bg-[var(--c-surface)]/80 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]">
            <Sidebar size={12} />
          </button>
          <button data-testid="toggle-right" onClick={() => setRightOpen((v) => !v)}
            className="absolute right-1 top-1 z-20 h-6 w-6 flex items-center justify-center rounded-sm bg-[var(--c-surface)]/80 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]">
            <Sidebar size={12} style={{ transform: "scaleX(-1)" }} />
          </button>
          <div className="flex-1 relative min-h-0">
            {jobList.length > 0 && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 w-[380px] max-w-[92%] space-y-2" data-testid="job-progress-area">
                {jobList.slice(0, 3).map((j) => (
                  <DrawingJobProgress key={j.job_id} job={j} onCancel={cancel} onRetry={retry} onDismiss={dismiss}
                    onPrioritise={(id) => prioritise(id).then(() => toast.success("Queue cleared — this sheet is generating now")).catch((e) => toast.error(fmtErr(e)))} />
                ))}
              </div>
            )}
            {doc?.type === "ga" && !view3d && (
              <button data-testid="mfg-sheet-btn" onClick={openMfgSheet}
                className="absolute top-2 right-2 z-30 h-7 px-2.5 rounded-sm bg-[var(--c-surface)]/95 border border-[var(--c-accent)]/50 text-[var(--c-accent)] text-[11px] hover:bg-[var(--c-accent)]/15">
                MFG Sheet (3D-derived)
              </button>
            )}
            {mkPanelOpen && (
              <div data-testid="markups-panel" className="absolute bottom-[4.5rem] right-2 z-50 w-80 max-h-[55%] bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm shadow-xl flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--c-border)] shrink-0">
                  <span className="text-xs font-medium text-[var(--c-text)]">Markups &amp; comments</span>
                  <button data-testid="markup-report-btn"
                    onClick={() => window.open(`${BASE}/api/projects/${projectId}/markups/report.xlsx`, "_blank")}
                    className="ml-auto h-6 px-2 rounded-sm bg-[var(--c-accent)]/15 border border-[var(--c-accent)]/50 text-[var(--c-accent)] text-[10px] hover:bg-[var(--c-accent)]/25">
                    Resolution sheet
                  </button>
                </div>
                <div className="overflow-y-auto min-h-0">
                  {markups.length === 0 && (
                    <div className="px-3 py-4 text-[11px] text-[var(--c-muted)]">No markups yet — use the pin tool (2D) or Markup mode (3D, works exploded too).</div>
                  )}
                  {markups.map((mk) => (
                    <button key={mk.id} data-testid={`markup-row-${mk.seq}`} onClick={() => openMarkup(mk)}
                      className="w-full flex items-start gap-2 px-3 py-2 text-left border-b border-[var(--c-border)]/40 last:border-0 hover:bg-[var(--c-border)]/25 transition-colors duration-100">
                      <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center font-mono text-[9px] ${mk.status === "resolved" ? "bg-[var(--c-green)]/20 text-[var(--c-green)]" : "bg-[var(--c-amber)]/20 text-[var(--c-amber)]"}`}>{mk.seq}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[11px] text-[var(--c-text)] truncate">{mk.comment}</span>
                        <span className="block text-[9px] text-[var(--c-muted)] font-mono uppercase">{mk.kind}{mk.owner_name ? ` · ${mk.owner_name}` : ""} · {mk.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="markups-toggle" onClick={() => setMkPanelOpen((v) => {
                const next = !v;
                if (next) window.dispatchEvent(new CustomEvent("fg:copilot-close"));
                return next;
              })}
              className="absolute bottom-10 right-2 z-50 h-7 px-2.5 rounded-sm bg-[var(--c-surface)]/95 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] text-[11px]">
              Markups ({markups.filter((m) => m.status === "open").length})
            </button>
            {mkDetail && (
              <div data-testid="markup-detail" className="absolute bottom-2 right-2 z-40 w-72 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm shadow-xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[var(--c-text)]" data-testid="markup-title">Markup #{mkDetail.seq}</span>
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${mkDetail.status === "resolved" ? "text-[var(--c-green)] border-[var(--c-green)]/40 bg-[var(--c-green)]/10" : "text-[var(--c-amber)] border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10"}`} data-testid="markup-status">{mkDetail.status}</span>
                  <span className="text-[9px] font-mono uppercase text-[var(--c-muted)]">{mkDetail.kind} · {mkDetail.severity}</span>
                  <button data-testid="markup-detail-close" onClick={() => setMkDetail(null)} className="ml-auto text-[var(--c-muted)] hover:text-[var(--c-text)]"><X size={12} /></button>
                </div>
                <p className="text-[11px] text-[var(--c-text)] leading-snug" data-testid="markup-comment">{mkDetail.comment}</p>
                <div className="text-[10px] text-[var(--c-muted)]">
                  {mkDetail.owner_name ? <>Owner: <span className="text-[var(--c-text)]" data-testid="markup-owner">{mkDetail.owner_name}</span> · </> : ""}
                  by {mkDetail.created_by} · {String(mkDetail.created_at || "").slice(0, 10)}
                </div>
                {mkDetail.status === "resolved" && mkDetail.resolution && (
                  <div className="text-[10px] text-[var(--c-green)]" data-testid="markup-resolution">Resolution: {mkDetail.resolution} — {mkDetail.resolved_by}</div>
                )}
                <div className="flex gap-1.5 pt-1">
                  {mkDetail.status === "open" ? (
                    <button data-testid="markup-resolve-btn" onClick={() => setMarkupStatus(mkDetail, "resolved")}
                      className="h-6 px-2.5 rounded-sm bg-[var(--c-green)]/20 border border-[var(--c-green)]/50 text-[var(--c-green)] text-[11px]">Resolve</button>
                  ) : (
                    <button data-testid="markup-reopen-btn" onClick={() => setMarkupStatus(mkDetail, "open")}
                      className="h-6 px-2.5 rounded-sm bg-[var(--c-amber)]/15 border border-[var(--c-amber)]/50 text-[var(--c-amber)] text-[11px]">Reopen</button>
                  )}
                  <button data-testid="markup-delete-btn" onClick={() => deleteMarkup(mkDetail)}
                    className="h-6 px-2.5 rounded-sm border border-[var(--c-red)]/40 text-[var(--c-red)] text-[11px] hover:bg-[var(--c-red)]/10">Delete</button>
                </div>
              </div>
            )}
            <MarkupDialog open={!!mkDialog} context={mkDialog} users={orgUsers} onSave={saveMarkup} onClose={() => setMkDialog(null)} />
            {showMfgSheet && (
              <div className="absolute inset-2 z-40 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm flex flex-col" data-testid="mfg-sheet-overlay">
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--c-border)]">
                  <span className="text-[11px] font-medium text-[var(--c-text)]">Manufacturing GA Sheet — derived from the 3D model (FreeCAD TechDraw)</span>
                  <a href={`${BASE}/api/drawings/${projectId}/print-ga.dxf`} target="_blank" rel="noreferrer" data-testid="mfg-sheet-dxf"
                    className="ml-auto text-[10px] font-mono text-[var(--c-accent)] hover:underline">DXF</a>
                  <button data-testid="mfg-sheet-close" onClick={() => setShowMfgSheet(false)}
                    className="text-[var(--c-muted)] hover:text-[var(--c-text)] text-[11px] px-1.5">Close</button>
                </div>
                <div className="flex-1 overflow-auto bg-[#10151c] flex items-start justify-center p-2">
                  {!mfgImgLoaded && (
                    <div className="flex items-center gap-2 text-[var(--c-muted)] text-xs mt-10">
                      <SpinnerGap size={16} className="animate-spin" /> Loading sheet (large SVG)…
                    </div>
                  )}
                  <img src={`${BASE}/api/drawings/${projectId}/print-ga.svg`} alt="Manufacturing GA sheet"
                    onLoad={() => setMfgImgLoaded(true)}
                    style={mfgImgLoaded ? undefined : { display: "none" }}
                    className="max-w-none" data-testid="mfg-sheet-img" />
                </div>
              </div>
            )}
            {symbolsView ? (
              <div className="absolute inset-0 bg-[var(--c-bg)]" data-testid="studio-symbology">
                <SymbologyLibrary compact />
              </div>
            ) : view3d ? (
              <Model3dPanel projectId={projectId} generate={generate} isGenerating={isGenerating} />
            ) : doc ? (
              <StudioCanvas elements={elements} ghosts={ghosts} tool={tool} activeLayer={activeLayer}
                layerVis={layerVis} showGrid={showGrid} locked={locked}
                selection={selection} setSelection={setSelection} commit={commit}
                onViewChange={(v) => setZoomPct(Math.round(v.s * 100))}
                onCursor={setCursor} stageRef={stageRef} canvasApi={canvasApi}
                pendingText={pendingText} setPendingText={setPendingText} setTool={setTool}
                onMarkupPoint={(pt) => {
                  if (locked) { toast.error("Drawing is locked — markups need an editable draft"); return; }
                  setMkDialog({ kind: "2d", pos: { x: Math.round(pt.x), y: Math.round(pt.y) } });
                }} />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--c-bg)]" data-testid="studio-empty">
                <div className="text-sm text-[var(--c-text)]">Select a drawing from the tree</div>
                <div className="text-xs text-[var(--c-muted)] mt-1">or Generate All to build the full drawing set.</div>
              </div>
            )}
          </div>
        </div>
        {rightOpen && (
          <div className="w-[300px] shrink-0 border-l border-[var(--c-border)] bg-[var(--c-surface)] flex flex-col min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto border-b border-[var(--c-border)]">
              <PropertiesPanel selected={selEls} drawing={doc} elementsCount={elements.length}
                onChange={onPropsChange} locked={locked} />
            </div>
            <div className="flex-1 min-h-0">
              <StudioCopilot projectId={projectId} drawing={doc} onOps={onOps} />
            </div>
          </div>
        )}
      </div>

      <StatusBar cursor={cursor} drawing={doc} elementsCount={elements.length}
        activeLayer={activeLayer} saveState={saveState} />
    </div>
  );
}
